import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSidenav } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { ResourceCreatorService } from '../../../services/resource-creator.service';
import { ResourceMetaData } from '../../../models/resource-meta-data';
import { ResourceModel } from '../../../models/resource-model';
import { InstallationSummary, TemplateInfoCardData } from '../../shared/template-info-card/template-info-card.component';
import { SoftwareInstallationStatusConstants, SoftwareInstallationStatusValue } from '../../../models/software-installation-status.constants';
import { SoftwareInstallationStatusEntry } from '../../../models/resource-model';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { UnifiedVmLauncherService } from '../../../services/unified-vm-launcher.service';
import { ClassEntityService } from '../../../services/entity-services/classes-entity.service';
import { interval, takeWhile, switchMap, catchError, of } from 'rxjs';
import { OrgTemplateAadService, OrgTemplateAadStatus } from '../../../services/org-template-aad.service';
import { NotificationService } from '../../../services/notification.service';
import { UaCredentialsCardComponent } from './ua-credentials-card/ua-credentials-card.component';
// Import for tooltip directive
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Router, ActivatedRoute } from '@angular/router';
import { IShortLogger, Log } from '../../../services/logger.service';
import { DrawerCategoryGroup, DrawerPayload, DrawerService } from '../../../services/drawer.service';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { ResourceMasterDataService } from '../../../services/resource-master-data.service';
import { UserPrefsService } from '../../../services/user-prefs.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../services/auth.service';
import { isParticipant } from '../../../utils/auth.utils';

// Maximum polling attempts before treating a 'creating' status as stale and stopping.
// 120 polls × 15 s = 30 minutes. Org-level template provisioning should never exceed this.
const MAX_POLLING_ATTEMPTS = 120;

/** UserPrefs page key for the templates info banner. */
const BANNER_PAGE = 'templates';

@SidebarNavMenuItem({
  name: "Templates",
  faIcon: "grid_view",
  route: "/templates",
  order: 1200,
  roles: ['admin'],
  businessProcessesName: 'Template Creation'
})
@Component({
  selector: 'app-resource-cards',
  templateUrl: './resource-cards.component.html',
  styleUrl: './resource-cards.component.css'
})
export class ResourceCardsComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly logger: IShortLogger<ResourceCardsComponent> = Log.create(ResourceCardsComponent);

  /** `true` when the current user is a management-team admin and may archive entities. */
  get canArchive(): boolean { return this.authService.canArchive(); }

  constructor(
    public service: ResourceCreatorService,
    private router: Router,
    private route: ActivatedRoute,
    private vmLauncher: UnifiedVmLauncherService,
    private classEntityService: ClassEntityService,
    private confirmDialog: ConfirmDialogService,
    private masterData: ResourceMasterDataService,
    private notification: NotificationService,
    private orgTemplateAadService: OrgTemplateAadService,
    private userPrefs: UserPrefsService,
    private snackBar: MatSnackBar,
    protected readonly drawerService: DrawerService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.getAllResources();
    this.loadOrgTemplateSubscriptions();

    // Show banner only for operator users who have not permanently dismissed it.
    this.bannerVisible =
      !isParticipant(this.authService.getCurrentUserValue()) &&
      !this.userPrefs.isBannerDismissed(BANNER_PAGE);

    // Wire the page-level installation-status drawer.
    this.drawerSubs.add(
      this.drawerService.open$.subscribe(payload => {
        this.drawerPayload = payload;
        this.rcDrawer.open();
      })
    );
    this.drawerSubs.add(
      this.drawerService.close$.subscribe(() => this.rcDrawer.close())
    );
  }

  /** Applies an optional ?tab= query param to jump straight to a specific resource type tab. */
  private applyTabQueryParam(): void {
    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam) {
      this.activeTab = tabParam;
    }
  }

  ngAfterViewInit() {
    // Check for templates that might already be in creation state
    this.checkExistingCreationStates();
  }

  public getAllResources() {
    this.service.getAll().subscribe((res) => {
      this.models = res;
      this.logger.i('Loaded resource models:', this.models);

      // Group models by type
      this.groupModelsByType();

      // Apply tab from query param (after grouping so the tab is respected even for new types)
      this.applyTabQueryParam();

      // Check for templates already in creation state after loading
      this.checkExistingCreationStates();

      this.isPageLoading = false;
    }, (err) => {
      this.logger.e('Failed to load resource models:', err);
      this.isPageLoading = false;
    })
  }

  public isPageLoading = true;
  public models: ResourceModel[] = [];
  public groupedModels: Map<string, ResourceModel[]> = new Map();
  public templateTypes: { type: string, name: string, icon: string }[] = [];
  public activeTab: string = 'vm'; // Default to VM tab
  public loadingTemplates: Set<string> = new Set();
  public actioningTemplates: Set<string> = new Set();

  // ── Banner state ──────────────────────────────────────────────────────────
  bannerVisible = false;
  bannerDismissForever = false;

  /** Hides the info banner; if "Don't show again" is checked, persists the dismissal. */
  dismissBanner(): void {
    this.bannerVisible = false;
    if (this.bannerDismissForever) {
      this.userPrefs.dismissBanner(BANNER_PAGE);
    }
  }

  /** Org-level template subscriptions — loaded once on init to gate the Create VM button. */
  public orgTemplateSubs: any[] = [];

  /** True when at least one org-level template subscription is configured. */
  get hasOrgTemplateSubscriptions(): boolean {
    return this.orgTemplateSubs.length > 0;
  }

  /** True when at least one org-level User Account (acc) template subscription is configured. */
  get hasUaTemplateSubscriptions(): boolean {
    // UA templates share the same subscription pool as VM templates for now.
    // Return true when any template subscription exists.
    return this.orgTemplateSubs.length > 0;
  }

  private loadOrgTemplateSubscriptions(): void {
    this.classEntityService.getAllTemplateSubscription().subscribe({
      next: (subs: any[]) => { this.orgTemplateSubs = subs ?? []; },
      error: () => { this.orgTemplateSubs = []; }
    });
  }
  private pollingStreams: Map<string, any> = new Map();

  /** Current Azure VM power state per template id ("running", "deallocated", "stopped", ...). */
  public vmPowerStates: Map<string, string> = new Map();
  /** Template ids whose password field is currently unmasked. */
  public shownPasswords: Set<string> = new Set();
  /** Active power-state polling subscriptions (for page-refresh recovery). */
  private powerStatePolls: Map<string, Subscription> = new Map();
  /** Template ids whose VM is currently being destroyed (synchronous HTTP call in flight). */
  public deletingTemplates: Set<string> = new Set();

  // ── AAD User lifecycle state (User Accounts tab) ──────────────────────────
  /** Map of templateId → latest AAD status record. Null means no record exists yet (idle). */
  public aadStatus: Record<string, OrgTemplateAadStatus | null> = {};
  /** Active AAD status poll subscriptions keyed by templateId. */
  private aadPolls = new Map<string, Subscription>();
  /** Map of templateId → TAP regeneration in-flight flag. */
  public regeneratingTap: Record<string, boolean> = {};

  /** Reference to the page-level installation status drawer. */
  @ViewChild('rcDrawer', { static: true }) private readonly rcDrawer!: MatSidenav;

  /** Currently displayed drawer payload; null before first open. */
  drawerPayload: DrawerPayload | null = null;

  /** Composite subscription for drawer open/close streams — cleaned up on destroy. */
  private readonly drawerSubs = new Subscription();

  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;
  /** QueryList of all rendered credentials cards — used to refresh individual cards after TAP regen. */
  @ViewChildren(UaCredentialsCardComponent) credentialCards!: QueryList<UaCredentialsCardComponent>;

  expandAllPanels(): void {
    this.expansionPanels.forEach(p => p.open());
  }

  collapseAllPanels(): void {
    this.expansionPanels.forEach(p => p.close());
  }

  /** Returns the current power state label for display. Empty string when unknown/not provisioned. */
  getPowerState(templateId: string): string {
    return this.vmPowerStates.get(templateId) ?? '';
  }

  /** True if the VM is currently in a transitional Azure state (starting/stopping/deallocating). */
  isPowerStateTransitional(templateId: string): boolean {
    const s = this.vmPowerStates.get(templateId) ?? '';
    return ['starting', 'stopping', 'deallocating'].includes(s);
  }

  togglePasswordVisibility(templateId: string): void {
    if (this.shownPasswords.has(templateId)) {
      this.shownPasswords.delete(templateId);
    } else {
      this.shownPasswords.add(templateId);
    }
  }

  copyToClipboard(value: string | undefined, label: string): void {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      this.notification.notify(`${label} copied`, 'success', 1500);
    });
  }

  /**
   * Fetches Azure VM power state once and stores it in vmPowerStates.
   * If the state is transitional, starts a polling loop until it settles.
   */
  private refreshVmPowerState(templateId: string): void {
    this.classEntityService.getOrgTemplateVmPowerState(templateId).subscribe({
      next: (res) => {
        this.vmPowerStates.set(templateId, res.powerState);
        if (this.isPowerStateTransitional(templateId)) {
          this.startPowerStatePolling(templateId);
        }
      },
      error: () => this.vmPowerStates.set(templateId, 'unknown')
    });
  }

  /**
   * Polls power state every 10 s until a terminal state is reached
   * (running / stopped / deallocated / notfound / unknown).
   * Adds to actioningTemplates while transitional so buttons stay disabled.
   */
  private startPowerStatePolling(templateId: string): void {
    if (this.powerStatePolls.has(templateId)) return;

    this.actioningTemplates.add(templateId);
    const transitionalStates = new Set(['starting', 'stopping', 'deallocating']);

    const sub = interval(10000).pipe(
      switchMap(() => this.classEntityService.getOrgTemplateVmPowerState(templateId)),
      takeWhile(res => transitionalStates.has(res.powerState), true),
      catchError(() => of({ powerState: 'unknown', templateId }))
    ).subscribe({
      next: (res) => {
        this.vmPowerStates.set(templateId, res.powerState);
        if (!transitionalStates.has(res.powerState)) {
          this.actioningTemplates.delete(templateId);
          this.powerStatePolls.delete(templateId);
          this.getAllResources();
        }
      },
      complete: () => {
        this.actioningTemplates.delete(templateId);
        this.powerStatePolls.delete(templateId);
      }
    });
    this.powerStatePolls.set(templateId, sub);
  }

  /**
   * Group models by their metaData.type
   */
  private groupModelsByType(): void {
    this.groupedModels.clear();
    this.templateTypes = [];

    // Group models by type
    this.models.forEach(model => {
      const type = model.metaData?.type || 'other';
      if (!this.groupedModels.has(type)) {
        this.groupedModels.set(type, []);
      }
      this.groupedModels.get(type)!.push(model);
    });

    // Create type metadata for tabs
    this.groupedModels.forEach((models, type) => {
      const typeInfo = this.getTypeInfo(type);
      this.templateTypes.push(typeInfo);
    });

    // Set active tab to first available type
    if (this.templateTypes.length > 0 && !this.groupedModels.has(this.activeTab)) {
      this.activeTab = this.templateTypes[0].type;
    }

    this.logger.i('Grouped models by type:', Array.from(this.groupedModels.entries()));
  }

  /**
   * Get display info for template type
   */
  private getTypeInfo(type: string): { type: string, name: string, icon: string } {
    const typeMap: { [key: string]: { name: string, icon: string } } = {
      'vm': { name: 'Virtual Machines', icon: 'monitor' },
      'acc': { name: 'User Accounts', icon: 'manage_accounts' },
      'other': { name: 'Other', icon: 'grid_view' }
    };

    const info = typeMap[type] || typeMap['other'];
    return { type, ...info };
  }

  /**
   * Get models for active tab
   */
  getModelsForActiveTab(): ResourceModel[] {
    return this.groupedModels.get(this.activeTab) || [];
  }

  /** Returns the index of the active tab for mat-tab-group binding. */
  getActiveTabIndex(): number {
    const idx = this.templateTypes.findIndex(t => t.type === this.activeTab);
    return idx >= 0 ? idx : 0;
  }

  /** Updates activeTab when the user clicks a mat-tab. */
  setActiveTabByIndex(index: number): void {
    if (this.templateTypes[index]) {
      this.activeTab = this.templateTypes[index].type;
    }
  }

  /**
   * Check if a type is a VM template
   */
  isVMTemplate(model: ResourceModel): boolean {
    return model.metaData?.type === 'vm';
  }

  /**
   * Check if a type is an Account template
   */
  isAccountTemplate(model: ResourceModel): boolean {
    return model.metaData?.type === 'acc';
  }

  /**
   * Returns the mapped Azure role name for a User Account template, or empty string.
   */
  getAccountRole(model: ResourceModel): string {
    const ua = model.data?.['UserAccountData'];
    if (!ua?.selectedCloudProvider || !ua?.selectedAccessLevel || !ua?.providerMappings) return '';
    return ua.providerMappings[ua.selectedCloudProvider]?.builtInRoles?.[ua.selectedAccessLevel] ?? '';
  }

  /**
   * Returns the asset URL for the cloud provider logo used in User Account template cards.
   * Uses the cloudprovider directory where files are named by provider id (e.g. azure.png).
   */
  getProviderLogoUrl(model: ResourceModel): string {
    const provider: string = model.data?.['UserAccountData']?.selectedCloudProvider
      ?? model.data?.['CloudProviderData']?.provider ?? '';
    return `assets/images/resources/cloudprovider/${provider}.png`;
  }

  /**
   * Resolves the human-readable OS name for a VM template model.
   * Looks up the osVersion id in the resource master data.
   */
  getOsName(model: ResourceModel): string {
    const sw = model.data?.['SoftwareData'];
    if (!sw?.os || !sw?.osVersion) return '';
    const versions = this.masterData.get<{ id: string; name: string }[]>(`software.os.${sw.os}.versions`) ?? [];
    return versions.find(v => v.id === sw.osVersion)?.name ?? sw.osVersion;
  }

  /**
   * Returns true when the model has at least one software item in any category.
   */
  hasSoftware(model: ResourceModel): boolean {
    const sw = model.data?.['SoftwareData'];
    if (!sw) return false;
    return (sw.sdks?.length > 0) || (sw.databases?.length > 0) ||
      (sw.editors?.length > 0) || (sw.misc?.length > 0);
  }

  /**
   * Resolves SDK IDs to human-readable names from master data.
   */
  getSdkNames(model: ResourceModel): string[] {
    const sdkIds: string[] = model.data?.['SoftwareData']?.sdks ?? [];
    const allSdks = this.masterData.get<{ id: string; name: string }[]>('software.sdks') ?? [];
    return sdkIds.map(id => allSdks.find(s => s.id === id)?.name ?? id);
  }

  /**
   * Resolves database IDs to human-readable names from master data.
   */
  getDatabaseNames(model: ResourceModel): string[] {
    const dbIds: string[] = model.data?.['SoftwareData']?.databases ?? [];
    const allDbs = this.masterData.get<{ id: string; name: string }[]>('software.databases') ?? [];
    return dbIds.map(id => allDbs.find(d => d.id === id)?.name ?? id);
  }

  /**
   * Resolves editor IDs to human-readable names from master data.
   */
  getEditorNames(model: ResourceModel): string[] {
    const editorIds: string[] = model.data?.['SoftwareData']?.editors ?? [];
    const allEditors = this.masterData.get<{ id: string; name: string }[]>('software.editors') ?? [];
    return editorIds.map(id => allEditors.find(e => e.id === id)?.name ?? id);
  }

  /**
   * Resolves misc tool IDs to human-readable names from master data.
   */
  getMiscNames(model: ResourceModel): string[] {
    const miscIds: string[] = model.data?.['SoftwareData']?.misc ?? [];
    const allMisc = this.masterData.get<{ id: string; name: string }[]>('software.misc') ?? [];
    return miscIds.map(id => allMisc.find(m => m.id === id)?.name ?? id);
  }

  /** Returns SDK items as { id, name } pairs for status lookup + display. */
  getSdkItems(model: ResourceModel): { id: string; name: string }[] {
    const ids: string[] = model.data?.['SoftwareData']?.sdks ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.sdks') ?? [];
    return ids.map(id => ({ id, name: all.find(s => s.id === id)?.name ?? id }));
  }

  /** Returns database items as { id, name } pairs for status lookup + display. */
  getDatabaseItems(model: ResourceModel): { id: string; name: string }[] {
    const ids: string[] = model.data?.['SoftwareData']?.databases ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.databases') ?? [];
    return ids.map(id => ({ id, name: all.find(d => d.id === id)?.name ?? id }));
  }

  /** Returns editor items as { id, name } pairs for status lookup + display. */
  getEditorItems(model: ResourceModel): { id: string; name: string }[] {
    const ids: string[] = model.data?.['SoftwareData']?.editors ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.editors') ?? [];
    return ids.map(id => ({ id, name: all.find(e => e.id === id)?.name ?? id }));
  }

  /** Returns misc-tool items as { id, name } pairs for status lookup + display. */
  getMiscItems(model: ResourceModel): { id: string; name: string }[] {
    const ids: string[] = model.data?.['SoftwareData']?.misc ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.misc') ?? [];
    return ids.map(id => ({ id, name: all.find(m => m.id === id)?.name ?? id }));
  }

  /**
   * Resolves the userKey for SoftwareInstallationStatuses display.
   * Uses the first key in customerCloudIds, or 'org_template' as fallback.
   */
  getInstallUserKey(model: ResourceModel): string {
    return 'org_template';
  }

  /** Returns the install-status map for the resolved userKey. */
  getInstallStatusMap(
    model: ResourceModel
  ): Record<string, Record<string, SoftwareInstallationStatusEntry>> {
    const key = this.getInstallUserKey(model);
    return model.softwareInstallationStatuses?.[key] ?? {};
  }

  /** True when there is at least one installation status entry to display. */
  hasInstallationStatus(model: ResourceModel): boolean {
    return Object.keys(this.getInstallStatusMap(model)).length > 0;
  }

  /**
   * Returns the installation status value for a specific software item.
   * Falls back to PENDING if no entry exists.
   */
  getInstallStatus(
    model: ResourceModel,
    category: string,
    softwareId: string
  ): SoftwareInstallationStatusValue {
    const entry = this.getInstallStatusMap(model)?.[category]?.[softwareId];
    if (!entry) return SoftwareInstallationStatusConstants.PENDING;
    return entry.status as SoftwareInstallationStatusValue;
  }

  /** Returns the error string for a failed item, or empty string. */
  getInstallError(model: ResourceModel, category: string, softwareId: string): string {
    return this.getInstallStatusMap(model)?.[category]?.[softwareId]?.error ?? '';
  }

  /**
   * Computes the overall installation status summary for a model:
   * - "failed" if any item is failed
   * - "pending" if any item is pending (and none failed)
   * - "installed" if all items are installed
   * - "no-status" if no installation status data exists
   */
  getInstallationSummaryStatus(model: ResourceModel): 'failed' | 'pending' | 'installed' | 'no-status' {
    const statusMap = this.getInstallStatusMap(model);
    if (Object.keys(statusMap).length === 0) {
      return 'no-status';
    }

    let hasFailed = false;
    let hasPending = false;

    // Check all categories and their items
    for (const category of Object.keys(statusMap)) {
      for (const softwareId of Object.keys(statusMap[category])) {
        const status = statusMap[category][softwareId].status;
        if (status === SoftwareInstallationStatusConstants.FAILED) {
          hasFailed = true;
        } else if (status === SoftwareInstallationStatusConstants.PENDING) {
          hasPending = true;
        }
      }
    }

    if (hasFailed) return 'failed';
    if (hasPending) return 'pending';
    return 'installed';
  }

  /** Maps installation summary status values to their clean display labels (no emoji). */
  private readonly installationSummaryLabels: Record<string, string> = {
    failed: 'Some software failed to install',
    pending: 'Installation in progress',
    installed: 'All software installed successfully',
  };

  /**
   * Builds a structured {@link InstallationSummary} for the given ResourceModel.
   * Returns the status and a human-readable label with no emoji characters.
   */
  buildInstallationSummary(model: ResourceModel): InstallationSummary {
    const status = this.getInstallationSummaryStatus(model);
    return {
      status: status === 'no-status' ? 'pending' : status,
      label: this.installationSummaryLabels[status] ?? '',
    };
  }

  /**
   * Builds an accordion-ready data structure from softwareInstallationStatuses.
   * Returns an array of { category, items } sorted by category name, with items being
   * all software from that category with their install status.
   *
   * Format:
   * [
   *   { category: 'databases', icon: 'storage', items: [{ id, name, status, error }, ...] },
   *   { category: 'editors', icon: 'edit_note', items: [...] },
   *   { category: 'misc', icon: 'construction', items: [...] },
   *   { category: 'sdks', icon: 'code', items: [...] }
   * ]
   */
  getInstallationDrawerContent(model: ResourceModel): DrawerCategoryGroup[] {
    const statusMap = this.getInstallStatusMap(model);

    // Map category key to Material icon
    const categoryIcons: Record<string, string> = {
      sdks: 'code',
      databases: 'storage',
      editors: 'edit_note',
      misc: 'construction',
    };

    // Map category key to human-readable display name
    const categoryDisplayNames: Record<string, string> = {
      sdks: 'SDKs & Frameworks',
      databases: 'Databases',
      editors: 'IDE Editors',
      misc: 'Miscellaneous & Tools',
    };

    // Build category entries with items
    const categories: Record<string, Array<{ id: string; name: string; status: string; error: string }>> = {};

    // Iterate through categories in the status map
    for (const category of Object.keys(statusMap).sort()) {
      const categoryData = statusMap[category];
      const items: Array<{ id: string; name: string; status: string; error: string }> = [];

      // For each software in this category, get its name and status
      for (const softwareId of Object.keys(categoryData)) {
        const entry = categoryData[softwareId];
        // Get the human-readable name for this software
        const name = this.getSoftwareName(category, softwareId);
        items.push({
          id: softwareId,
          name: name || softwareId,
          status: entry.status,
          error: entry.error || ''
        });
      }

      categories[category] = items.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Convert to array format with icons and display names, in fixed order
    const categoryOrder = ['sdks', 'databases', 'editors', 'misc'];
    return categoryOrder
      .filter(category => categories[category] && categories[category].length > 0)
      .map(category => ({
        category: categoryDisplayNames[category] ?? category,
        icon: categoryIcons[category] || 'info',
        items: categories[category]
      }));
  }

  /** Maps software category keys to their master-data lookup keys. */
  private readonly categoryMasterKeys: Record<string, string> = {
    sdks: 'software.sdks',
    databases: 'software.databases',
    editors: 'software.editors',
    misc: 'software.misc',
  };

  /**
   * Helper to get the human-readable name for a software item by category and ID.
   */
  private getSoftwareName(category: string, softwareId: string): string {
    const masterKey = this.categoryMasterKeys[category] ?? '';
    if (!masterKey) return '';
    const items = this.masterData.get<{ id: string; name: string }[]>(masterKey) ?? [];
    return items.find(item => item.id === softwareId)?.name ?? '';
  }
  getCloudProviderName(model: ResourceModel): string {
    const provider: string = model.data?.['CloudProviderData']?.provider;
    if (!provider) return '';
    return this.masterData.get<string>(`cloud.${provider}.name`) ?? provider;
  }

  /**
   * Resolves the cloud region ID to its human-readable name from master data.
   */
  getRegionName(model: ResourceModel): string {
    const cloudData = model.data?.['CloudProviderData'];
    if (!cloudData?.provider || !cloudData?.region) return '';
    const regions = this.masterData.get<{ id: string; name: string }[]>(`cloud.${cloudData.provider}.regions`) ?? [];
    return regions.find(r => r.id === cloudData.region)?.name ?? cloudData.region;
  }

  /**
   * Resolves the VM size ID to its human-readable name from master data.
   */
  getSizeName(model: ResourceModel): string {
    const cloudData = model.data?.['CloudProviderData'];
    if (!cloudData?.provider || !cloudData?.size) return '';
    const sizes = this.masterData.get<{ id: string; name: string }[]>(`cloud.${cloudData.provider}.sizes`) ?? [];
    return sizes.find(s => s.id === cloudData.size)?.name ?? cloudData.size;
  }

  /**
   * Builds a {@link TemplateInfoCardData} object for the given ResourceModel,
   * mapping resource-cards helpers to the shared component's flat data contract.
   *
   * @param model - The VM template ResourceModel to map.
   * @returns     {@link TemplateInfoCardData} ready for binding to app-template-info-card.
   */
  buildTemplateInfoCardData(model: ResourceModel): TemplateInfoCardData {
    return {
      name: model.data['TemplateDetailsData']?.templateName ?? model.metaData.name,
      cardSubtitle: model.data['TemplateDetailsData']?.templateDescription
        ?? model.metaData.description
        ?? 'Below are the details of the template VM',
      provider: this.getCloudProviderName(model),
      region: this.getRegionName(model),
      vmSize: this.getSizeName(model),
      osLabel: this.getOsName(model),
      sdks: this.getSdkNames(model),
      editors: this.getEditorNames(model),
      tools: this.getMiscNames(model),
      databases: this.getDatabaseNames(model),
      status: model.status,
      installationSummary: this.hasInstallationStatus(model)
        ? this.buildInstallationSummary(model)
        : undefined,
      isLoading: this.isTemplateLoading(model.id),
    };
  }

  /**
   * Generates tooltip content for arrays of items
   */
  getTooltipContent(items: string[]): string {
    if (!items || items.length === 0) return '';

    const listItems = items.map(item => `<li>${item}</li>`).join('');
    return `<ul class="tooltip-list">${listItems}</ul>`;
  }

  /**
   * Generates tooltip content specifically for participants
   */
  getParticipantsTooltip(participants: string[]): string {
    if (!participants || participants.length === 0) return 'No participants';

    const listItems = participants.map(participant => `<li>${participant}</li>`).join('');
    return `<ul class="tooltip-list">${listItems}</ul>`;
  }

  /**
   * Triggers org-template VM provisioning. Called by the "Create VM" button.
   * Does NOT open a window — calls the backend to start async VM creation, then
   * starts polling. When polling detects the VM is ready, launchReadyTemplate()
   * opens the Guacamole session automatically.
   */
  provisionOrgTemplateVm(model: ResourceModel): void {
    if (!model.id) {
      this.logger.e('Model ID is required for org template provisioning');
      return;
    }

    if (this.isTemplateLoading(model.id) || this.pollingStreams.has(model.id)) {
      this.logger.w('Provisioning already in progress for template, ignoring click');
      return;
    }

    this.loadingTemplates.add(model.id);

    // Reflect creating state locally so canCreateVm() returns false immediately.
    const modelIndex = this.models.findIndex(m => m.id === model.id);
    if (modelIndex !== -1) {
      this.models[modelIndex].status = 'creating';
    }

    this.classEntityService.getTemplateLaunchDetails(model.id).subscribe({
      next: (response) => {
        if (response?.status === 'wait-for-creation') {
          // Backend acknowledged provisioning — poll until the VM is ready.
          this.logger.i(`Org template ${model.id} provisioning initiated. Starting status poll.`);
          this.startPollingTemplateStatus(model.id);
        } else {
          // Template was already provisioned (edge case) — open session directly.
          this.logger.i(`Org template ${model.id} already provisioned. Launching session.`);
          this.cleanupTemplateState(model.id);
          this.launchReadyTemplate(model.id);
        }
      },
      error: (error) => {
        this.logger.e('Failed to initiate template provisioning:', error);
        this.cleanupTemplateState(model.id);
        const msg = error?.error?.message
          ?? (error.status === 400
            ? 'No subscription with available quota. Please check Template VM Subscriptions.'
            : 'Failed to start VM provisioning. Please try again.');
        alert(msg);
      },
    });
  }

  launch(model: ResourceModel) {
    // Connects to an already-provisioned template VM (opens a Guacamole session).
    // Only call this when isVmProvisioned(model) is true.

    if (!model.id) {
      this.logger.e('Model ID is required for template launch');
      return;
    }

    // Prevent multiple launches if already in progress
    if (this.isTemplateLoading(model.id)) {
      this.logger.w('Template creation already in progress, ignoring click');
      return;
    }

    // Check if already polling for this template
    if (this.pollingStreams.has(model.id)) {
      this.logger.w('Polling already active for template, ignoring click');
      return;
    }

    // Check if template is already being created
    if (model.status === 'creating') {
      this.logger.i('Template already being created, starting polling instead');
      this.loadingTemplates.add(model.id);
      this.startPollingTemplateStatus(model.id);
      return;
    }

    // Immediately add to loading set and mark as creating to prevent multiple clicks
    this.loadingTemplates.add(model.id);

    // Update local model status to 'creating' immediately for UI feedback
    const modelIndex = this.models.findIndex(m => m.id === model.id);
    if (modelIndex !== -1) {
      this.models[modelIndex].status = 'creating';
    }

    // Show loading state for template creation/launch
    const statusText = model.status === 'draft' ? 'Creating and launching template VM...' : 'Launching template VM...';
    this.logger.i(statusText);

    const userId = localStorage.getItem('userId') ?? '';
    const userName = localStorage.getItem('userName') ?? '';
    const preOpenedWindow = window.open('', '_blank');
    this.vmLauncher.launchOrgTemplateVm({ userId, templateId: model.id, userName, vmConnectionTemplateId: model.vmConnectionTemplateId ?? null }, preOpenedWindow)
      .then(() => {
        this.cleanupTemplateState(model.id);
        this.getAllResources();
      })
      .catch((error: any) => {
        this.logger.e('Template launch failed:', error);
        this.cleanupTemplateState(model.id);
        alert('Failed to launch template VM.');
      });
  }
  public addTemplate() {
    this.router.navigate(['/create']);
  }
  id: string | undefined = undefined;



  /** @deprecated Use canLaunch(model) / canDelete(model) instead of these shared flags. */
  isVisibleLaunchButton: boolean = false;
  /** @deprecated Use canLaunch(model) / canDelete(model) instead of these shared flags. */
  isVisibleDeleteButton: boolean = false;

  /** Currently open card — used so visibility helpers are evaluated against the correct model. */
  openCard: ResourceModel | undefined = undefined;

  /**
   * Tracks per-resource in-flight VM Connection Template attach/detach saves so the shared
   * `<app-vm-connection-template-attach>` control on each card can be disabled while its own
   * save is pending. A Set rather than a single id flag because multiple cards may be expanded
   * simultaneously and saved concurrently.
   */
  readonly savingVmConnectionTemplateId = new Set<string>();

  /**
   * Persists the user's attach/detach choice from the per-card shared attach control.
   * Optimistically mirrors the new value onto the local `model` so subsequent reads stay
   * consistent without a refetch round-trip; rolls back on error.
   */
  onResourceVmConnectionTemplateChange(model: ResourceModel, newId: string | null): void {
    if (!model?.id) return;
    const previous = model.vmConnectionTemplateId ?? null;
    model.vmConnectionTemplateId = newId;
    this.savingVmConnectionTemplateId.add(model.id);

    this.service.assignVmConnectionTemplate(model.id, newId).subscribe({
      next: () => {
        this.savingVmConnectionTemplateId.delete(model.id);
        this.notification.notify(
          newId ? 'Connection template attached.' : 'Connection template detached.',
          'success', 2500);
      },
      error: (err: any) => {
        // Roll back the optimistic mutation so the dropdown reflects the persisted state.
        model.vmConnectionTemplateId = previous;
        this.savingVmConnectionTemplateId.delete(model.id);
        this.notification.notify(
          err?.error?.message ?? 'Failed to update connection template.',
          'error', 3000);
      },
    });
  }

  /** Returns true when the card should show the Test VM action. */
  canLaunch(card: ResourceModel): boolean {
    return card?.metaData?.type?.toLowerCase() === 'vm';
  }

  /** Returns true when the card should show the Destroy action. */
  canDelete(card: ResourceModel): boolean {
    return card?.cloudIds != null && Object.keys(card.cloudIds).length > 0;
  }

  toggleMenu(event: Event, card: ResourceModel): void {
    // Keep legacy flags in sync for any template that still references them.
    this.isVisibleLaunchButton = this.canLaunch(card);
    this.isVisibleDeleteButton = this.canDelete(card);

    event.stopPropagation(); // Prevent the click from propagating to document

    if (this.id !== card.id) {
      this.id = card.id;
      this.openCard = card;
    } else {
      this.id = undefined;
      this.openCard = undefined;
    }
  }

  finalize(id: string) {
    this.service.finalize(id).subscribe((res: any) => {
      this.logger.i('Resource finalized successfully');
    }, (err) => {
      this.logger.e('Error finalizing resource:', err);
    });
  }
  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    // Only perform the check if a menu is currently open
    if (this.id !== undefined) {
      // Check if the click was on a menu toggle button (the ellipsis icon)
      const isMenuToggleClick = (event.target as HTMLElement).classList.contains('menu-icon');

      // If it wasn't a click on the menu toggle button itself
      if (!isMenuToggleClick) {
        // Check if the click was inside the dropdown menu
        const isClickInsideMenu = (event.target as HTMLElement).closest('.dropdown-menu') !== null;

        // If the click was outside the menu, close it
        if (!isClickInsideMenu) {
          this.id = undefined;
        }
      }
    }
  }

  /**
   * Starts polling template creation status every 15 seconds.
   * Stops automatically after MAX_POLLING_ATTEMPTS (module constant) to prevent infinite loops
   * when the org-level Status is stuck at 'creating' with no active provisioning.
   */
  private startPollingTemplateStatus(templateId: string): void {
    // Check if already polling for this template
    if (this.pollingStreams.has(templateId)) {
      this.logger.w(`Polling already active for template ${templateId}, skipping`);
      return;
    }

    this.logger.i(`Starting status polling for template ${templateId}`);

    let pollCount = 0;

    // Poll every 15 seconds
    const polling$ = interval(15000).pipe(
      switchMap(() => this.classEntityService.getTemplateStatus(templateId)),
      takeWhile((statusResponse) => {
        pollCount++;
        this.logger.i(`Template ${templateId} status (attempt ${pollCount}):`, statusResponse);

        if (pollCount >= MAX_POLLING_ATTEMPTS) {
          this.logger.w(`Template ${templateId} polling stopped after ${pollCount} attempts — status still '${statusResponse.status}'. Possible stale status in DB.`);
          return false;
        }

        // Continue polling only while the VM is actively being created.
        // active+isProvisioned means ready; active+!isProvisioned can only happen for
        // acc-type templates (which never enter this path), so treat it as done.
        const shouldContinue = statusResponse.status === 'creating';

        if (!shouldContinue) {
          this.logger.i(`Stopping polling for template ${templateId}. Final status:`, statusResponse.status);
        }

        return shouldContinue;
      }, true), // Include the last value when condition becomes false
      catchError((error) => {
        this.logger.e(`Error polling template ${templateId} status:`, error);
        this.cleanupTemplateState(templateId);
        return of(null); // Stop polling on error
      })
    );

    // Store the subscription to track active polling
    const subscription = polling$.subscribe({
      next: (statusResponse) => {
        if (statusResponse) {
          // Update the model status in our local array
          const modelIndex = this.models.findIndex(m => m.id === templateId);
          if (modelIndex !== -1) {
            this.models[modelIndex].status = statusResponse.status;
          }

          // If template is ready, try to launch it
          if (statusResponse.status === 'active' && statusResponse.isProvisioned) {
            this.logger.i(`Template ${templateId} is ready, attempting to launch...`);
            this.launchReadyTemplate(templateId);
          } else if (statusResponse.status === 'failed') {
            this.logger.e(`Template ${templateId} creation failed`);
            this.cleanupTemplateState(templateId);
            alert('Template creation failed. Please check the logs and try again.');
          }
        }
      },
      complete: () => {
        this.logger.i(`Polling completed for template ${templateId}`);
        this.cleanupTemplateState(templateId);
        this.getAllResources();
      }
    });

    // Track the subscription
    this.pollingStreams.set(templateId, subscription);
  }

  /**
   * Launches a template that is ready (provisioned)
   */
  private launchReadyTemplate(templateId: string): void {
    const userId = localStorage.getItem('userId') ?? '';
    const userName = localStorage.getItem('userName') ?? '';
    const preOpenedWindow = window.open('', '_blank');
    // Look up the model so we can forward the per-resource VM Connection Template id.
    const model = this.models.find(m => m.id === templateId);
    this.vmLauncher.launchOrgTemplateVm(
      { userId, templateId, userName, vmConnectionTemplateId: model?.vmConnectionTemplateId ?? null },
      preOpenedWindow
    )
      .then(() => {
        this.cleanupTemplateState(templateId);
        this.getAllResources();
      })
      .catch((error: any) => {
        this.logger.e(`Failed to launch ready template ${templateId}:`, error);
        this.cleanupTemplateState(templateId);
        alert('Template is ready but failed to launch. Please try the Test VM action again.');
      });
  }

  /**
   * Polls the backend every 10 s until the org-template VM's status leaves 'destroying'.
   * Clears the in-memory deletion guards and refreshes the resource list when the
   * transition is detected so the UI auto-updates without a manual page reload.
   * Idempotent — does nothing if a poll is already running for this template.
   */
  private startDestroyingPoll(templateId: string): void {
    const pollKey = `${templateId}:destroy`;
    if (this.pollingStreams.has(pollKey)) return;

    const sub = interval(10000).pipe(
      switchMap(() => this.classEntityService.getTemplateStatus(templateId)),
      takeWhile(res => res.status === 'destroying', true),
      catchError(err => {
        this.logger.e(`Destroying poll error for template ${templateId}:`, err);
        return of({ status: 'unknown', isProvisioned: false, templateId, lastUpdated: '', message: '' });
      })
    ).subscribe({
      next: (res) => {
        if (res.status !== 'destroying') {
          this.deletingTemplates.delete(templateId);
          this.actioningTemplates.delete(templateId);
          this.pollingStreams.delete(pollKey);
          this.getAllResources();
        }
      },
      complete: () => {
        this.deletingTemplates.delete(templateId);
        this.actioningTemplates.delete(templateId);
        this.pollingStreams.delete(pollKey);
      }
    });
    this.pollingStreams.set(pollKey, sub);
  }

  /**
   * Cleanup template state: remove from loading set and stop polling
   */
  private cleanupTemplateState(templateId: string): void {
    this.logger.i(`Cleaning up template state for ${templateId}`);

    // Remove from loading templates
    this.loadingTemplates.delete(templateId);

    // Unsubscribe and remove polling stream
    const subscription = this.pollingStreams.get(templateId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingStreams.delete(templateId);
      this.logger.i(`Stopped and removed polling stream for template ${templateId}`);
    }
  }

  /**
   * Checks if a template is currently loading/being created
   */
  public isTemplateLoading(templateId: string): boolean {
    return this.loadingTemplates.has(templateId);
  }

  /**
   * Gets the loading status text for a template
   */
  public getLoadingStatusText(model: ResourceModel): string {
    if (!this.isTemplateLoading(model.id)) {
      return '';
    }

    const statusTextMap = new Map([
      ['creating', 'Creating Virtual Machine...'],
      ['active', 'Finalizing setup...']
    ]);

    return statusTextMap.get(model.status) || 'Processing...';
  }

  /**
   * Checks for templates that are already in creation state on page load
   * Also fetches current status from backend for all templates
   */
  private checkExistingCreationStates(): void {
    this.logger.i('Checking existing creation states for all templates...');

    // VM provisioning status polling is only relevant for vm-type templates.
    // acc-type templates (User Accounts) have a separate AAD lifecycle and are
    // always status='active' + isProvisioned=false — if included here they enter
    // an infinite 15-second poll loop that never terminates.
    const vmModels = this.models.filter(m => m.metaData?.type === 'vm');
    const accModels = this.models.filter(m => m.metaData?.type === 'acc');

    // Prefetch AAD status for every User Account template so the badge is
    // visible on page load without needing to expand each accordion panel.
    accModels.forEach(model => this.refreshAadStatus(model.id));

    vmModels.forEach(model => {
      // Fetch current status from backend for each VM template
      this.classEntityService.getTemplateStatus(model.id).subscribe({
        next: (statusResponse) => {
          this.logger.i(`Template ${model.id} current status:`, statusResponse);

          // Do NOT overwrite model.status here — the authoritative status comes from
          // getAllResources() which reads the backend resource model directly.
          // Overwriting here with the class-template provisioning vocabulary
          // (creating / active / failed) corrupts the org-resource lifecycle
          // status (draft / active / destroyed) visible on the resource cards.

          // If VM template is mid-provisioning, start polling.
          if (statusResponse.status === 'creating') {
            this.logger.i(`Template ${model.id} is in creation state, starting polling`);
            this.loadingTemplates.add(model.id);
            this.startPollingTemplateStatus(model.id);
            return;
          }

          // If VM template is mid-destruction (navigated away before the HTTP call resolved),
          // re-arm the in-memory guards and start a poll that clears them when done.
          // This ensures the Destroy VM button and power controls stay hidden and a second
          // destroy call cannot be submitted through the UI (CAS is still the backend guard).
          if (statusResponse.status === 'destroying') {
            this.logger.i(`Template ${model.id} is in destroying state, re-arming deletion guard`);
            this.actioningTemplates.add(model.id);
            this.deletingTemplates.add(model.id);
            this.startDestroyingPoll(model.id);
            return; // don't probe Azure power state while a destruction is in progress
          }

          // Pre-emptive stale detection: if the backend liveness probe found the Azure VM
          // gone and cleared the DB state (isProvisioned=false, status=draft), refresh the
          // resource list so the accordion card immediately shows the updated state instead
          // of keeping the stale cloudIds/status visible until the next manual page reload.
          const modelHadLiveCloudIds = model.cloudIds != null && Object.keys(model.cloudIds).length > 0;
          if (!statusResponse.isProvisioned && modelHadLiveCloudIds && statusResponse.status === 'draft') {
            this.logger.w(`Template ${model.id} was stale in DB — reloading resource list after probe cleared state`);
            this.getAllResources();
            return; // remaining logic for this model is redundant after a full reload
          }

          // Check live Azure power state for provisioned VM templates.
          if (statusResponse.isProvisioned) {
            this.refreshVmPowerState(model.id);
          }
        },
        error: (error) => {
          this.logger.e(`Error fetching status for template ${model.id}:`, error);
          // If status fetch fails, fall back to checking local model status.
          if (model.status === 'creating') {
            this.logger.i(`Fallback: Found template ${model.id} in creation state, starting polling`);
            this.loadingTemplates.add(model.id);
            this.startPollingTemplateStatus(model.id);
          }
        }
      });
    });
  }

  /**
   * Cleanup all polling streams when component is destroyed
   */
  ngOnDestroy(): void {
    this.logger.i('ResourceCardsComponent destroying, cleaning up polling streams');

    this.pollingStreams.forEach((subscription, templateId) => {
      this.logger.i(`Unsubscribing polling stream for template ${templateId}`);
      subscription.unsubscribe();
    });
    this.pollingStreams.clear();
    this.loadingTemplates.clear();

    this.powerStatePolls.forEach(sub => sub.unsubscribe());
    this.powerStatePolls.clear();
    this.deletingTemplates.clear();

    this.aadPolls.forEach(sub => sub.unsubscribe());
    this.aadPolls.clear();

    this.drawerSubs.unsubscribe();
  }

  // ── AAD User Controls (User Accounts tab) ────────────────────────────────

  /**
   * Fetches the current AAD status for a template and stores it in aadStatus.
   * A 404 response is treated as "no record yet" (idle state) rather than an error.
   * If the status is transitional (Provisioning/Deleting), starts a background poll.
   */
  refreshAadStatus(templateId: string): void {
    this.orgTemplateAadService.getStatus(templateId).subscribe({
      next: status => {
        this.aadStatus[templateId] = status;
        if (this.isAadTransitioning(status?.status)) {
          this.startAadPolling(templateId);
        }
      },
      error: (err: any) => {
        if (err.status === 404) {
          // No AAD record yet — leave as null so idle state renders
          this.aadStatus[templateId] = null;
        } else {
          this.notification.error('Failed to load AAD status');
        }
      }
    });
  }

  /**
   * Starts a 3-second poll loop for the given template's AAD status.
   * Stops automatically when the status reaches a terminal state (Active/Failed/Deleted).
   * Idempotent — does nothing if a poll is already running for this template.
   */
  startAadPolling(templateId: string): void {
    if (this.aadPolls.has(templateId)) return;
    const sub = this.orgTemplateAadService.pollUntilTerminal(templateId).subscribe({
      next: status => { this.aadStatus[templateId] = status; },
      complete: () => { this.aadPolls.delete(templateId); }
    });
    this.aadPolls.set(templateId, sub);
  }

  /** Triggers AAD user provisioning for a User Account template. Allows retry on 'Failed' status. */
  provisionAadUser(model: ResourceModel): void {
    const currentStatus = this.aadStatus[model.id]?.status;

    // Guard: prevent double-submit while actively provisioning or deleting.
    // Allow retry from 'Failed' state, or provision from idle state.
    if (currentStatus === 'Provisioning' || currentStatus === 'Deleting') {
      return;
    }

    this.aadStatus[model.id] = { status: 'Provisioning' } as any;

    this.orgTemplateAadService.triggerProvision(model.id).subscribe({
      next: status => {
        this.aadStatus[model.id] = status;
        this.startAadPolling(model.id);
      },
      error: () => this.notification.error('Failed to trigger AAD user provisioning')
    });
  }

  /**
   * Opens a typed-confirmation dialog requiring the user to type DELETE before
   * the AAD user is removed. Replaces the previous snackbar-confirm pattern.
   */
  confirmDeleteAadUser(model: ResourceModel): void {
    const templateName = model.data?.['TemplateDetailsData']?.templateName ?? model.metaData.name;
    this.confirmDialog.typedConfirm({
      title: `Delete AAD User — ${templateName}`,
      message: `This will permanently delete the Azure AD user and revoke all subscription access assigned to this template.`,
      details: [
        'The AAD user account will be deleted from Azure Active Directory.',
        'All subscription role assignments for this user will be removed.',
        'Any resources created by this user must be cleaned up manually.',
        'This action cannot be undone.',
      ],
      type: 'error',
      confirmPhrase: 'DELETE',
      confirmButtonText: 'Delete AAD User',
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.orgTemplateAadService.triggerDelete(model.id).subscribe({
        next: () => {
          this.aadStatus[model.id] = { ...this.aadStatus[model.id]!, status: 'Deleting' };
          this.startAadPolling(model.id);
        },
        error: () => this.notification.error('Failed to trigger AAD user deletion')
      });
    });
  }

  /** Returns true when the AAD status is in a transitional (non-terminal) state. */
  isAadTransitioning(status?: string | null): boolean {
    return status === 'Provisioning' || status === 'Deleting';
  }

  /** Regenerates the Temporary Access Pass for the given template's AAD user. */
  regenerateTap(templateId: string): void {
    this.confirmDialog.confirm({
      title: 'Regenerate Temporary Access Pass?',
      message: 'A new TAP will be issued and the current one will be immediately invalidated. ' +
        'Anyone using the existing pass will need the new one to sign in.',
      confirmText: 'Regenerate TAP',
      cancelText: 'Cancel',
      type: 'warning',
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.regeneratingTap[templateId] = true;
      this.orgTemplateAadService.regenerateTap(templateId).subscribe({
        next: () => {
          this.regeneratingTap[templateId] = false;
          this.openStandardSnackbar('TAP regenerated successfully.');
          // Refresh the matching credentials card so the new TAP displays immediately.
          const card = this.credentialCards?.find(c => c.templateId === templateId);
          card?.loadCredentials();
        },
        error: () => {
          this.regeneratingTap[templateId] = false;
          this.openStandardSnackbar('Failed to regenerate TAP', 5000);
        }
      });
    });
  }

  private openStandardSnackbar(message: string, duration = 3000): void {
    this.snackBar.open(message, 'Dismiss', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  /** Opens the Azure Portal in a new browser tab for template validation. */
  openAzurePortal(): void {
    window.open('https://portal.azure.com', '_blank', 'noopener,noreferrer');
  }

  /** Returns true when no AAD record exists or the user has been deleted. */
  isAadIdle(status?: string | null): boolean {
    return !status || status === 'Deleted';
  }

  /** Returns a human-readable label for an AAD status value. */
  getAadStatusLabel(status?: string | null): string {
    const labels: Record<string, string> = {
      Provisioning: 'Provisioning',
      Active: 'Active',
      Failed: 'Failed',
      Deleting: 'Deleting',
      Deleted: 'Not provisioned'
    };
    return labels[status ?? ''] ?? 'Not provisioned';
  }

  /** Returns CSS class map for an AAD status badge. */
  getAadStatusBadgeClass(status?: string | null): Record<string, boolean> {
    return {
      'status-badge--active': status === 'Active',
      'status-badge--pending': status === 'Provisioning' || status === 'Deleting',
      'status-badge--inactive': status === 'Failed',
      'status-badge--draft': !status || status === 'Deleted'
    };
  }

  // deleteTemplate(model: ResourceModel) {

  // }

  /** Returns true when the given template's VM is provisioned (CloudIds non-empty). */
  public isVmProvisioned(model: ResourceModel): boolean {
    return model.cloudIds != null && Object.keys(model.cloudIds).length > 0;
  }

  /** Returns true when a power action (start/shutdown/stop/destroy) is running for this template. */
  public isActioning(templateId: string): boolean {
    return this.actioningTemplates.has(templateId);
  }

  /** Returns true when the VM destruction HTTP call is in-flight for this template. */
  public isDeleting(templateId: string): boolean {
    return this.deletingTemplates.has(templateId);
  }

  /** Returns a human-readable label for the current actioning state. */
  public getActioningLabel(templateId: string): string {
    if (this.isDeleting(templateId)) { return 'Deleting VM…'; }
    const powerState = this.vmPowerStates.get(templateId) ?? '';
    const stateLabels = new Map<string, string>([
      ['starting', 'Starting VM…'],
      ['stopping', 'Shutting down VM…'],
      ['deallocating', 'Stopping & deallocating VM…'],
    ]);
    return stateLabels.get(powerState) ?? 'Action in progress…';
  }

  /** Returns true when the Create VM button should be shown/enabled. */
  public canCreateVm(model: ResourceModel): boolean {
    if (this.isActioning(model.id)) return false;
    return model.status === 'draft' || model.status === 'failed' || model.status === 'destroyed';
  }

  startOrgTemplateVm(model: ResourceModel): void {
    if (this.isActioning(model.id) || this.isTemplateLoading(model.id)) return;

    const templateName = model.data?.['TemplateDetailsData']?.templateName ?? model.metaData.name;
    const vmName = model.cloudIds?.['vmname'];

    this.confirmDialog.typedConfirm({
      title: `Start Org Template VM — ${templateName}`,
      message: `Start VM "${vmName ?? templateName}". Azure compute charges will resume while the VM is running.`,
      type: 'info',
      confirmPhrase: 'START',
      confirmButtonText: 'Start VM',
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.actioningTemplates.add(model.id);
      this.vmPowerStates.set(model.id, 'starting');
      this.classEntityService.startOrgTemplateVm(model.id).subscribe({
        next: () => {
          this.actioningTemplates.delete(model.id);
          this.notification.notify('VM started successfully', 'success', 3000);
          this.refreshVmPowerState(model.id);
          this.getAllResources();
        },
        error: (err) => {
          this.actioningTemplates.delete(model.id);
          this.logger.e('Failed to start org-template VM:', err);
          this.notification.notify('Failed to start VM. Please try again.', 'error', 3000);
          this.refreshVmPowerState(model.id);
        }
      });
    });
  }

  shutdownOrgTemplateVm(model: ResourceModel): void {
    if (this.isActioning(model.id) || this.isTemplateLoading(model.id)) return;

    const templateName = model.data?.['TemplateDetailsData']?.templateName ?? model.metaData.name;
    const vmName = model.cloudIds?.['vmname'];

    this.confirmDialog.typedConfirm({
      title: `Shutdown Org Template VM — ${templateName}`,
      message: `Gracefully shuts down the guest OS on VM "${vmName ?? templateName}". The VM stays allocated — Azure charges continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN',
      confirmButtonText: 'Shutdown VM'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.actioningTemplates.add(model.id);
      this.vmPowerStates.set(model.id, 'stopping');
      this.classEntityService.shutdownOrgTemplateVm(model.id).subscribe({
        next: () => {
          this.actioningTemplates.delete(model.id);
          this.notification.notify('VM shutdown signal sent successfully', 'success', 3000);
          this.refreshVmPowerState(model.id);
          this.getAllResources();
        },
        error: (err) => {
          this.actioningTemplates.delete(model.id);
          this.logger.e('Failed to shutdown org-template VM:', err);
          this.notification.notify('Failed to shutdown VM. Please try again.', 'error', 3000);
          this.refreshVmPowerState(model.id);
        }
      });
    });
  }

  stopOrgTemplateVm(model: ResourceModel): void {
    if (this.isActioning(model.id) || this.isTemplateLoading(model.id)) return;

    const templateName = model.data?.['TemplateDetailsData']?.templateName ?? model.metaData.name;
    const vmName = model.cloudIds?.['vmname'];

    this.confirmDialog.typedConfirm({
      title: `Stop & Deallocate Org Template VM — ${templateName}`,
      message: `Gracefully shuts down the guest OS then deallocates VM "${vmName ?? templateName}". Azure compute charges pause; storage charges continue.`,
      type: 'warning',
      confirmPhrase: 'STOP',
      confirmButtonText: 'Stop & Deallocate'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.actioningTemplates.add(model.id);
      this.vmPowerStates.set(model.id, 'deallocating');
      this.classEntityService.stopOrgTemplateVm(model.id).subscribe({
        next: () => {
          this.actioningTemplates.delete(model.id);
          this.notification.notify('VM stopped and deallocated successfully', 'success', 3000);
          this.refreshVmPowerState(model.id);
          this.getAllResources();
        },
        error: (err) => {
          this.actioningTemplates.delete(model.id);
          this.logger.e('Failed to stop org-template VM:', err);
          this.notification.notify('Failed to stop VM. Please try again.', 'error', 3000);
          this.refreshVmPowerState(model.id);
        }
      });
    });
  }

  // Execute the actual deletion of a specific template
  deleteOrgTemplate(model: ResourceModel) {
    // Idempotency gate: block when a destroy is already in-flight (in this session or from a
    // previous session where the browser was navigated away — status === 'destroying').
    if (this.isDeleting(model.id) || this.isActioning(model.id)) return;

    const templateName = model.data['TemplateDetailsData']?.templateName ?? model.metaData.name;
    const vmName = model.cloudIds?.['vmname'];
    const subscriptionId = model.cloudIds?.['subscriptionid'];
    const resourceGroupName = model.cloudIds?.['resourcegroupname'];

    this.confirmDialog.typedConfirm({
      title: `Destroy Org Template VM — ${templateName}`,
      message: `Permanently deletes all Azure resources for template "${templateName}" by removing the resource group. This cannot be undone.`,
      details: [
        `Template: ${templateName}`,
        ...(vmName ? [`VM Name: ${vmName}`] : []),
        ...(subscriptionId ? [`Subscription: ${subscriptionId}`] : []),
        ...(resourceGroupName ? [`Resource Group: ${resourceGroupName}`] : []),
        'The entire resource group (VM, disk, NIC, Public IP, and all other resources) will be permanently deleted.',
      ],
      type: 'error',
      confirmPhrase: 'DESTROY',
      confirmButtonText: 'Destroy VM',
      confirmSummary: 'The resource group and all resources inside it will be permanently deleted.',
    }).subscribe(result => {
      if (!result.confirmed) {
        return;
      }
      this.executeDeleteOrgTemplate(model);
    });
  }

  private executeDeleteOrgTemplate(model: ResourceModel) {
    const templateName = model.data['TemplateDetailsData']?.templateName ?? model.metaData.name;
    this.notification.notify(`Starting deletion of template "${templateName}"...`, 'warning', 3000);

    // Track delete state separately so the UI can show "Deleting VM…" rather than the
    // generic "Action in progress…" text used for start / stop / shutdown actions.
    this.actioningTemplates.add(model.id);
    this.deletingTemplates.add(model.id);

    this.classEntityService.destroyOrgTemplate(model.id).subscribe({
      next: (_response) => {
        // Backend returned 202: RG deletion is running in the background.
        // Keep actioningTemplates / deletingTemplates set so the UI stays locked.
        // startDestroyingPoll polls every 10 s and clears the guards + refreshes
        // the resource list once the backend status leaves "destroying".
        this.vmPowerStates.delete(model.id);
        this.startDestroyingPoll(model.id);
      },
      error: (error) => {
        this.actioningTemplates.delete(model.id);
        this.deletingTemplates.delete(model.id);
        this.logger.e('Failed to delete template:', error);
        this.notification.notify(`Failed to delete template "${model.id}"`, 'error', 3000);
      }
    });
  }

  // ── Drawer status helpers ───────────────────────────────────────────────────────────────

  /** Closes the installation-status drawer via DrawerService. */
  closeDrawer(): void {
    this.drawerService.close();
  }

  /**
   * Prompts the user then archives the resource template via the API.
   * Shows a 409-specific message if linked classes are still active.
   */
  archiveTemplate(model: ResourceModel): void {
    const name = model.data?.['TemplateDetailsData']?.templateName ?? model.metaData?.name ?? model.id;

    this.confirmDialog.confirmAction(
      'Archive Template',
      `Archive "${name}"? Archived templates are hidden from the list.`,
      'warning',
      'Archive',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) return;

      this.service.archiveTemplate(model.id).subscribe({
        next: () => {
          this.notification.notify(`Template "${name}" archived.`, 'success', 3000);
          this.getAllResources();
        },
        error: (err: any) => {
          if (err?.status === 409) {
            this.snackBar.open(
              'Cannot archive: some linked classes are still active. Archive them first.',
              'Dismiss', { duration: 6000 }
            );
          } else {
            this.notification.notify('Failed to archive template.', 'error', 3000);
          }
        }
      });
    });
  }
}
